// Per-window section history for the code viewer. A location is a section
// (standard + item + start page), not every page scrolled through. Back drops
// the forward list when a new section is recorded, same as a browser.

import { standardId, standardName } from '../../utils/standardRef'

const HISTORY_LIMIT = 50

// Compact, serializable copy of an item — enough to reopen its PDF and label
// the hop. Full tree fields (path, authors) stay out of the persisted nav.
export function snapshot(item) {
  if (!item || typeof item !== 'object') return null
  const id = item._id || item.item
  if (!id) return null
  return {
    _id: id,
    label: item.label || 'Section',
    standard: item.standard && standardId(item.standard)
      ? { _id: standardId(item.standard), name: standardName(item.standard) }
      : item.standard || '',
    pageStart: Number.isFinite(item.pageStart) ? item.pageStart : 1,
    pageEnd: Number.isFinite(item.pageEnd) ? item.pageEnd : undefined,
    level: item.level,
    order: item.order,
    path: Array.isArray(item.path) ? item.path : [],
  }
}

// Restore a saved list. Windows saved before history existed start at the
// item they already had open, so Back has a first entry after the next hop.
export function coerceHistory(raw, open) {
  const seeded = snapshot(open)
  const list = Array.isArray(raw?.history)
    ? raw.history.map(snapshot).filter(Boolean).slice(-HISTORY_LIMIT)
    : []
  const history = list.length ? list : seeded ? [seeded] : []
  let historyIndex = Number.isInteger(raw?.historyIndex) ? raw.historyIndex : history.length - 1
  if (!history.length) historyIndex = -1
  else if (historyIndex >= history.length) historyIndex = history.length - 1
  else if (historyIndex < 0) historyIndex = 0
  return { history, historyIndex }
}

// Record `item` as the current location. Same section as the pointer is a
// no-op. Anything forward of the pointer is dropped.
export function pushHistory(nav, item) {
  const snap = snapshot(item)
  if (!snap) return nav
  const history = Array.isArray(nav.history) ? nav.history : []
  const index = Number.isInteger(nav.historyIndex) ? nav.historyIndex : -1
  if (history[index] && String(history[index]._id) === String(snap._id)) return nav
  let next = history.slice(0, index + 1)
  next.push(snap)
  if (next.length > HISTORY_LIMIT) next = next.slice(next.length - HISTORY_LIMIT)
  return { ...nav, history: next, historyIndex: next.length - 1 }
}

// Move the pointer. Same-standard hops keep `open` (the PDF stays mounted)
// and carry a `recall` the viewer applies as a page jump. A different standard
// replaces `open` so the window loads that document. Sidebar fields are left
// untouched because this spreads the rest of `nav` through.
export function stepHistory(nav, dir) {
  const history = Array.isArray(nav.history) ? nav.history : []
  const index = Number.isInteger(nav.historyIndex) ? nav.historyIndex : -1
  const nextIndex = index + dir
  if (nextIndex < 0 || nextIndex >= history.length) return nav
  const entry = history[nextIndex]
  const sameDoc =
    Boolean(standardId(entry.standard)) &&
    standardId(entry.standard) === standardId(nav.open?.standard)
  return {
    ...nav,
    historyIndex: nextIndex,
    open: sameDoc ? nav.open : entry,
    recall: {
      nonce: (nav.recall?.nonce || 0) + 1,
      itemId: entry._id,
      pageStart: entry.pageStart,
      standard: entry.standard,
    },
  }
}

// Tooltip text for one hop. Include the standard when it isn't the document
// currently open in the window.
export function historyHopLabel(entry, currentStandard) {
  if (!entry) return ''
  if (
    standardId(entry.standard) &&
    standardId(entry.standard) !== standardId(currentStandard)
  ) {
    return `${standardName(entry.standard) || 'Standard'} — ${entry.label}`
  }
  return entry.label || 'Section'
}
